import React, { useState, useEffect } from 'react';
import { tupoksiService } from '../../../services/tupoksiService';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaMinus, FaSearch, FaEye, FaEyeSlash
} from 'react-icons/fa';

const ManajemenTupoksi = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        jabatan: '',
        tingkat: '',
        tugas: '',
        uraian_tugas: [''],
        dasar_hukum: '',
        order: 0,
        status: 'publish',
    });

    const loadData = async () => {
        setLoading(true);
        const result = await tupoksiService.getTupoksiList({ search: searchTerm });
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            jabatan: '',
            tingkat: '',
            tugas: '',
            uraian_tugas: [''],
            dasar_hukum: '',
            order: dataList.length + 1,
            status: 'publish',
        });
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            jabatan: item.jabatan || '',
            tingkat: item.tingkat || '',
            tugas: item.tugas || '',
            uraian_tugas: item.uraian_tugas && item.uraian_tugas.length > 0 ? item.uraian_tugas : [''],
            dasar_hukum: item.dasar_hukum || '',
            order: item.order || 0,
            status: item.status || 'publish',
        });
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUraianChange = (index, value) => {
        const newUraian = [...formData.uraian_tugas];
        newUraian[index] = value;
        setFormData(prev => ({ ...prev, uraian_tugas: newUraian }));
    };

    const addUraian = () => {
        setFormData(prev => ({ ...prev, uraian_tugas: [...prev.uraian_tugas, ''] }));
    };

    const removeUraian = (index) => {
        if (formData.uraian_tugas.length <= 1) return;
        const newUraian = formData.uraian_tugas.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, uraian_tugas: newUraian }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanUraian = formData.uraian_tugas.filter(u => u.trim() !== '');
        if (cleanUraian.length === 0) {
            showNotif('Minimal satu uraian tugas harus diisi', 'error');
            return;
        }

        const submitData = { ...formData, uraian_tugas: cleanUraian };

        setSubmitting(true);
        let result;
        if (editing) {
            result = await tupoksiService.updateTupoksi(editing.id, submitData);
        } else {
            result = await tupoksiService.createTupoksi(submitData);
        }

        if (result.success) {
            showNotif(result.data?.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await tupoksiService.deleteTupoksi(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'publish' ? 'draft' : 'publish';
        const result = await tupoksiService.updateStatus(item.id, newStatus);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    const filteredData = dataList.filter(item =>
        item.jabatan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tugas?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Tupoksi</h2>
                    <p className="text-gray-600">Kelola data tugas pokok dan fungsi</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                    <FaPlus className="mr-2" /> Tambah Tupoksi
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Search */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari jabatan atau tugas..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">No</th>
                                <th className="p-4">Jabatan</th>
                                <th className="p-4">Tingkat</th>
                                <th className="p-4">Jumlah Uraian</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-gray-500">
                                        Belum ada data tupoksi
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4 text-gray-500">{index + 1}</td>
                                        <td className="p-4">
                                            <p className="font-semibold text-[#0F4C3A]">{item.jabatan}</p>
                                            {item.dasar_hukum && (
                                                <p className="text-xs text-gray-400">{item.dasar_hukum}</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs">
                                                {item.tingkat}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-sm text-gray-600">
                                                {item.uraian_tugas?.length || 0} uraian
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <button
                                                onClick={() => handleToggleStatus(item)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${item.status === 'publish'
                                                        ? 'bg-green-100 text-green-700'
                                                        : item.status === 'draft'
                                                            ? 'bg-yellow-100 text-yellow-700'
                                                            : 'bg-gray-100 text-gray-500'
                                                    }`}
                                            >
                                                {item.status === 'publish' ? 'Publish' : item.status === 'draft' ? 'Draft' : 'Arsip'}
                                            </button>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(item)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                    title="Hapus"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editing ? 'Edit Tupoksi' : 'Tambah Tupoksi'}
                                </h3>
                                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Jabatan <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="jabatan"
                                            value={formData.jabatan}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Kepala Badan"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tingkat <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="tingkat"
                                            value={formData.tingkat}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Eselon II.b"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tugas Pokok <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="tugas"
                                        value={formData.tugas}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Tuliskan tugas pokok..."
                                        required
                                    />
                                </div>

                                {/* Uraian Tugas */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Uraian Tugas <span className="text-red-500">*</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={addUraian}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] font-medium flex items-center gap-1"
                                        >
                                            <FaPlus size={12} /> Tambah Uraian
                                        </button>
                                    </div>

                                    <div className="space-y-2">
                                        {formData.uraian_tugas.map((uraian, index) => (
                                            <div key={index} className="flex gap-2 items-start">
                                                <span className="w-8 h-11 flex items-center justify-center bg-[#0F4C3A] text-white rounded-lg font-bold text-sm flex-shrink-0">
                                                    {index + 1}
                                                </span>
                                                <textarea
                                                    value={uraian}
                                                    onChange={(e) => handleUraianChange(index, e.target.value)}
                                                    rows="2"
                                                    className="flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                                    placeholder={`Uraian tugas ke-${index + 1}`}
                                                />
                                                {formData.uraian_tugas.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUraian(index)}
                                                        className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <FaMinus />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Dasar Hukum
                                        </label>
                                        <input
                                            type="text"
                                            name="dasar_hukum"
                                            value={formData.dasar_hukum}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Perwali No. 49 Tahun 2023"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urutan
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    >
                                        <option value="publish">Publish (Tampil di Website)</option>
                                        <option value="draft">Draft (Simpan Saja)</option>
                                        <option value="arsip">Arsip</option>
                                    </select>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Data?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus tupoksi <strong>{deleteConfirm.jabatan}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenTupoksi;